import { FormikProps } from 'formik';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { ActionButtonGroup, Responsive, SettingsButtons, TabContainer } from '@components';
import { FormActionType, FormType, PageRulesTab, Translation } from '@enums';
import {
  useFetchFormList,
  useFormHandlers,
  useFormLoadingStatus,
  useFormProcedure,
  useFormSubmissionStatus,
} from '@hooks';
import { getPageRulesTabs, validateValueEqual } from '@utils';

import { PredefinedPageRulesForm } from './subCards';

export const PageRules: FC = () => {
  const { id: application_id } = useParams();

  const formType = FormType.CdnPageRules;

  const { t: tTabs } = useTranslation(Translation.Configuration, { keyPrefix: 'tabs.pageRules' });
  const { t: tConfigurationButton } = useTranslation(Translation.Configuration, { keyPrefix: 'buttons' });

  const formProcedure = useFormProcedure(formType);
  const formSubmitting = useFormSubmissionStatus({ formType });
  const formLoading = useFormLoadingStatus({ formType });

  useEffect(() => {
    if (formikRef.current?.errors && Object.keys(formikRef.current.errors).length > 0) {
      formikRef.current.validateForm();
    }
  }, [tTabs]);

  useFetchFormList({
    formType,
    params: { application_id },
    globalOptions: { content: true },
  });

  const formikRef = useRef<FormikProps<any> | null>(null);

  const [activeTab, setActiveTab] = useState<PageRulesTab>(PageRulesTab.PAGE_RULES);

  const handleSaveSettings = useCallback(() => {
    formikRef?.current?.handleSubmit();
  }, []);

  const handleFormikInstance = useCallback((formik: FormikProps<any> | null) => {
    formikRef.current = formik;
  }, []);

  const PageRulesFormHandler = useFormHandlers({
    formType,
    handlerOptions: [
      {
        type: FormActionType.Save,
        function: handleSaveSettings,
        label: tConfigurationButton('saveSettings'),
        loading: formSubmitting,
        disabled: formLoading,
      },
    ],
  });

  const contentConfig = useCallback(() => {
    return {
      [PageRulesTab.PAGE_RULES]: <PredefinedPageRulesForm onFormikInstance={handleFormikInstance} />,
    };
  }, []);

  const PageRulesTabs = useMemo(() => getPageRulesTabs(), [tTabs]);

  return (
    <div className="w-full flex flex-col flex-1 gap-6 sm:gap-8">
      <TabContainer<PageRulesTab>
        tabs={PageRulesTabs}
        onTabChange={setActiveTab}
        contentConfig={contentConfig()}
        contentClassName="gap-6 sm:gap-8"
        adornment={
          <Responsive showAbove="sm">
            {validateValueEqual(activeTab, PageRulesTab.PAGE_RULES) && (
              <ActionButtonGroup actionHandlers={PageRulesFormHandler} procedure={formProcedure} />
            )}
          </Responsive>
        }
      />
      <Responsive showBelow="sm">
        {validateValueEqual(activeTab, PageRulesTab.PAGE_RULES) && (
          <SettingsButtons handleSaveModal={handleSaveSettings} formType={formType} />
        )}
      </Responsive>
    </div>
  );
};
